'use client';

import type { ContactSeoFaqItem } from '@repo/api';
import { Accordion } from '@/components/ui/Accordion';
import { Divider, SectionContainer, SectionHeader } from '@/components/ui';
import { cn } from '@/lib/cn';

interface Props {
  items: ContactSeoFaqItem[];
}

export function FAQ({ items }: Props) {
  if (!items?.length) return null;

  return (
    <SectionContainer>
      <SectionHeader title="FAQ" />

      {/* JSON-LD kept beside the visible FAQ so the two cannot drift apart. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map(item => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />

      {/* On md+, the FAQ spans 2 of a 3-column grid (left ~66%); the
          right column stays empty. Constraining text width keeps long
          answers readable instead of stretching across the full page.
          On mobile it's full-width as before. */}
      <div className="w-full md:grid md:grid-cols-3 md:gap-6 md:px-2">
        <div className="md:col-span-2 flex flex-col gap-1 items-start w-full">
          {items.map((item, index) => (
            <div key={index} className="w-full">
              <div className="flex flex-col items-start w-full">
                <Accordion
                  header={item.q}
                  headerClassName={cn('px-2')}
                  contentClassName={cn('px-2')}
                  closeOnOutsideClick={true}
                >
                  <p className="text-body-2 text-primary">{item.a}</p>
                </Accordion>
              </div>

              <div className="px-2">
                {index < items.length - 1 && <Divider variant="secondary" className="my-1" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionContainer>
  );
}
